import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import { DropdownData as US_STATES } from "../../../../../components/FormElements/AddressFields/DropdownState/DropdownState.data";
import { GoogleSearchAutoComplete } from "../../../../../components/FormElements/AddressFields/GoogleSearchAutoComplete/GoogleSearchAutoComplete";
import {
  AideRentalHistoryData,
  AideResidence,
  emptyLiveInAideState,
} from "../../liveInAideState";

// ---------------------------------------------------------------------------
// Screen 2 — Rental history for the Live-In Aide (RESPROP LIA Questionnaire
// p1 "Landlord Screening"). Two residence blocks: CURRENT (required — landlord
// + street address, plus city/state/zip) and PREVIOUS (optional as a whole,
// but once any previous field is filled we require its street address so staff
// can run the landlord reference). The street address uses the same
// GoogleSearchAutoComplete as the co-applicant Rental step (parity): picking a
// suggestion autofills street/city/state/zip, and every field stays editable as
// a manual fallback. Google Places returns the full state name, mapped to the
// 2-letter abbreviation the Select stores.
// ---------------------------------------------------------------------------

interface Props {
  /** Persisted rental-history slice (state.rentalHistory). */
  defaultValues?: Partial<AideRentalHistoryData>;
  onSubmit: (data: AideRentalHistoryData) => void | Promise<void>;
  isSaving?: boolean;
}

const zipRegex = /^\d{5}(-\d{4})?$/;

// Google Places returns the full state name (e.g. "Texas"); the state Select
// stores the 2-letter abbreviation. Map name -> abbreviation (falling back to an
// abbreviation match), else "" so the dropdown stays empty rather than wrong.
const stateNameToAbbr = (name?: string): string => {
  if (!name) return "";
  const match = US_STATES.find(
    (s) =>
      s.label.toLowerCase() === name.toLowerCase() ||
      s.abbreviation.toLowerCase() === name.toLowerCase()
  );
  return match ? match.abbreviation : "";
};

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits.length ? `(${digits}` : "";
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// A previous-residence block counts as "started" once any of its fields holds
// a value; that's when its address becomes required.
const isPreviousStarted = (prev?: Partial<AideResidence>): boolean =>
  !!prev &&
  Object.values(prev).some((v) => typeof v === "string" && v.trim() !== "");

export const AideRentalHistory: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const currentResidenceSchema = yup.object({
    landlord: yup
      .string()
      .required(
        t(
          "affordable.application.liveInAide.rentalHistory.landlordRequired"
        ) as string
      ),
    address: yup
      .string()
      .required(
        t(
          "affordable.application.liveInAide.rentalHistory.addressRequired"
        ) as string
      ),
    city: yup
      .string()
      .required(
        t(
          "affordable.application.liveInAide.rentalHistory.cityRequired"
        ) as string
      ),
    state: yup
      .string()
      .required(
        t(
          "affordable.application.liveInAide.rentalHistory.stateRequired"
        ) as string
      ),
    zip: yup
      .string()
      .required(
        t("affordable.application.liveInAide.rentalHistory.zipRequired") as string
      )
      .matches(
        zipRegex,
        t("affordable.application.liveInAide.rentalHistory.zipError") as string
      ),
    phone: yup.string().default(""),
    startDate: yup.string().default(""),
    endDate: yup.string().default(""),
  });

  // Previous residence is optional as a whole. Once the block is "started"
  // (any field filled), require its street address so the reference can run.
  const previousResidenceSchema = yup.object({
    landlord: yup.string().default(""),
    address: yup.string().when("$previousStarted", {
      is: true,
      then: (s) =>
        s.required(
          t(
            "affordable.application.liveInAide.rentalHistory.addressRequired"
          ) as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    city: yup.string().default(""),
    state: yup.string().default(""),
    zip: yup
      .string()
      .default("")
      .test(
        "zip-format",
        t("affordable.application.liveInAide.rentalHistory.zipError") as string,
        (val) => !val || zipRegex.test(val)
      ),
    phone: yup.string().default(""),
    startDate: yup.string().default(""),
    endDate: yup.string().default(""),
  });

  const schema = yup.object({
    current: currentResidenceSchema,
    previous: previousResidenceSchema,
  });

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<AideRentalHistoryData>({
    // The previous-address requirement is contextual: recompute the flag from
    // the live values at validation time via yup's context.
    resolver: (values, _context, options) =>
      yupResolver(schema)(
        values,
        { previousStarted: isPreviousStarted(values.previous) },
        options
      ),
    defaultValues: {
      current: {
        ...emptyLiveInAideState.rentalHistory.current,
        ...(defaultValues?.current ?? {}),
      },
      previous: {
        ...emptyLiveInAideState.rentalHistory.previous,
        ...(defaultValues?.previous ?? {}),
      },
    },
  });

  const renderResidenceFields = (prefix: "current" | "previous") => {
    const err =
      prefix === "current" ? errors.current : errors.previous;
    return (
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.landlord` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={
                  prefix === "current"
                    ? `${t(
                        "affordable.application.liveInAide.rentalHistory.landlordLabel"
                      )} *`
                    : (t(
                        "affordable.application.liveInAide.rentalHistory.landlordLabel"
                      ) as string)
                }
                error={!!err?.landlord}
                helperText={err?.landlord?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.phone` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={
                  t(
                    "affordable.application.liveInAide.rentalHistory.phoneLabel"
                  ) as string
                }
                placeholder="(555) 555-5555"
                inputProps={{ inputMode: "tel" }}
                onChange={(e) => field.onChange(formatPhone(e.target.value))}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`${prefix}.address` as const}
            control={control}
            render={({ field }) => (
              <GoogleSearchAutoComplete
                id={`aide-${prefix}-address`}
                label={
                  prefix === "current"
                    ? `${t(
                        "affordable.application.liveInAide.rentalHistory.addressLabel"
                      )} *`
                    : (t(
                        "affordable.application.liveInAide.rentalHistory.addressLabel"
                      ) as string)
                }
                value={field.value || ""}
                error={err?.address?.message}
                isValid={!err?.address}
                // Free typing keeps the manual entry path working as a fallback.
                onInputChange={(val: string) => field.onChange(val ?? "")}
                // Picking a suggestion autofills the whole address; every field
                // stays editable afterward.
                onSelect={(addr: {
                  line1?: string;
                  city?: string;
                  state?: string;
                  zip?: string;
                }) => {
                  field.onChange(addr.line1 || "");
                  setValue(`${prefix}.city` as const, addr.city || "");
                  setValue(
                    `${prefix}.state` as const,
                    stateNameToAbbr(addr.state)
                  );
                  setValue(`${prefix}.zip` as const, addr.zip || "");
                  trigger([
                    `${prefix}.address`,
                    `${prefix}.city`,
                    `${prefix}.state`,
                    `${prefix}.zip`,
                  ] as const);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.city` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={
                  prefix === "current"
                    ? `${t(
                        "affordable.application.liveInAide.rentalHistory.cityLabel"
                      )} *`
                    : (t(
                        "affordable.application.liveInAide.rentalHistory.cityLabel"
                      ) as string)
                }
                error={!!err?.city}
                helperText={err?.city?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Controller
            name={`${prefix}.state` as const}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!err?.state}>
                <InputLabel>
                  {prefix === "current"
                    ? `${t(
                        "affordable.application.liveInAide.rentalHistory.stateLabel"
                      )} *`
                    : (t(
                        "affordable.application.liveInAide.rentalHistory.stateLabel"
                      ) as string)}
                </InputLabel>
                <Select
                  {...field}
                  label={
                    prefix === "current"
                      ? `${t(
                          "affordable.application.liveInAide.rentalHistory.stateLabel"
                        )} *`
                      : (t(
                          "affordable.application.liveInAide.rentalHistory.stateLabel"
                        ) as string)
                  }
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {US_STATES.map((s) => (
                    <MenuItem key={s.abbreviation} value={s.abbreviation}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
                {err?.state && (
                  <FormHelperText>
                    {err.state.message as string}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Controller
            name={`${prefix}.zip` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={
                  prefix === "current"
                    ? `${t(
                        "affordable.application.liveInAide.rentalHistory.zipLabel"
                      )} *`
                    : (t(
                        "affordable.application.liveInAide.rentalHistory.zipLabel"
                      ) as string)
                }
                error={!!err?.zip}
                helperText={err?.zip?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.startDate` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={
                  t(
                    "affordable.application.liveInAide.rentalHistory.startDateLabel"
                  ) as string
                }
                placeholder="MM/YYYY"
                inputProps={{ inputMode: "numeric" }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.endDate` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={
                  t(
                    "affordable.application.liveInAide.rentalHistory.endDateLabel"
                  ) as string
                }
                placeholder="MM/YYYY"
                inputProps={{ inputMode: "numeric" }}
              />
            )}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.rentalHistory.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.rentalHistory.subtitle")}
        </Typography>

        <Typography
          color="primary"
          sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
        >
          {t("affordable.application.liveInAide.rentalHistory.currentTitle")}
        </Typography>
        {renderResidenceFields("current")}

        <Box sx={{ mt: 4 }}>
          <Typography
            color="primary"
            sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
          >
            {t(
              "affordable.application.liveInAide.rentalHistory.previousTitle"
            )}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {t("affordable.application.liveInAide.rentalHistory.previousNote")}
          </Typography>
          {renderResidenceFields("previous")}
        </Box>
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
